import { AppState, HarnessBackground, WireBundle, WireBundleSegment } from '../types';
import { BundleManager } from '../managers/BundleManager';
import { connectorHeight } from '../utils/connectorLayout';
import { CONNECTOR, COLORS, BUNDLE_GEOMETRY } from '../utils/constants';

/** Blank space kept around the harness inside the SVG viewBox. */
const PADDING = 60;
/** Radius of a routing-node dot. */
const NODE_RADIUS = 5;
/** Stroke width of a single-wire section; trunks scale up slightly with wire count. */
const WIRE_STROKE = 3;
const TRUNK_STROKE = 5;

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function grow(bounds: Bounds, x: number, y: number): void {
  bounds.minX = Math.min(bounds.minX, x);
  bounds.minY = Math.min(bounds.minY, y);
  bounds.maxX = Math.max(bounds.maxX, x);
  bounds.maxY = Math.max(bounds.maxY, y);
}

/** Escapes text for safe inclusion in SVG markup. */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function segmentSvg(seg: WireBundleSegment): string {
  const parts: string[] = [];
  const single = seg.wireCount <= 1;
  const color = single ? seg.color ?? COLORS.WIRE_BUNDLE : COLORS.WIRE_BUNDLE;
  const width = single ? WIRE_STROKE : TRUNK_STROKE;

  parts.push(
    `<line x1="${round(seg.startPoint.x)}" y1="${round(seg.startPoint.y)}" ` +
      `x2="${round(seg.endPoint.x)}" y2="${round(seg.endPoint.y)}" ` +
      `stroke="${color}" stroke-width="${width}" stroke-linecap="round" />`
  );

  (seg.stubs ?? []).forEach(stub => {
    parts.push(
      `<line x1="${round(stub.from.x)}" y1="${round(stub.from.y)}" ` +
        `x2="${round(stub.to.x)}" y2="${round(stub.to.y)}" ` +
        `stroke="${stub.color}" stroke-width="${WIRE_STROKE}" stroke-linecap="round" />`
    );
  });

  const midX = (seg.startPoint.x + seg.endPoint.x) / 2;
  const midY = (seg.startPoint.y + seg.endPoint.y) / 2;
  const lengthLabel = seg.length > 0 ? `${seg.length} mm` : '? mm';
  const label = seg.wireCount > 1 ? `${lengthLabel}  (×${seg.wireCount})` : lengthLabel;
  parts.push(
    `<text x="${round(midX)}" y="${round(midY - 6)}" text-anchor="middle" ` +
      `font-size="11" fill="${COLORS.SECONDARY}">${escapeXml(label)}</text>`
  );

  return parts.join('\n');
}

/**
 * The reference image as a backmost SVG layer, in world coordinates, when the
 * user has opted to show it in the report. Returns an empty string otherwise.
 */
function backgroundSvg(background?: HarnessBackground): string {
  if (!background || !background.showInReport) {
    return '';
  }
  const width = round(background.imageWidth * background.scale);
  const height = round(background.imageHeight * background.scale);
  return (
    `<image href="${background.src}" x="${round(background.x)}" y="${round(background.y)}" ` +
    `width="${width}" height="${height}" opacity="${background.opacity}" preserveAspectRatio="none" />`
  );
}

function bowSvg(bundle: WireBundle): string {
  return (bundle.bows ?? [])
    .map(bow => {
      const controlX = Math.min(bow.from.x, bow.to.x) - BUNDLE_GEOMETRY.BOW_BULGE;
      const controlY = (bow.from.y + bow.to.y) / 2;
      return (
        `<path d="M ${round(bow.from.x)} ${round(bow.from.y)} ` +
        `Q ${round(controlX)} ${round(controlY)} ${round(bow.to.x)} ${round(bow.to.y)}" ` +
        `fill="none" stroke="${bow.color}" stroke-width="${WIRE_STROKE}" stroke-linecap="round" />`
      );
    })
    .join('\n');
}

/**
 * Renders the harness as a self-contained, decluttered schematic SVG string:
 * connector cards, routing nodes, and the shared wire trunks with per-section
 * lengths. Reuses {@link BundleManager} so the geometry matches the canvas.
 */
export function buildHarnessSvg(state: AppState): string {
  const bundleManager = new BundleManager();
  bundleManager.updateBundles(state.wires, state.connectors, state.nodes, state.segments);
  const bundles = bundleManager.getBundles();

  const bounds: Bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };

  const connectorBoxes = state.connectors.map(connector => {
    const height = connectorHeight(connector, state.wires);
    grow(bounds, connector.xPosition, connector.yPosition);
    grow(bounds, connector.xPosition + CONNECTOR.WIDTH, connector.yPosition + height);
    return { connector, height };
  });

  state.nodes.forEach(node => grow(bounds, node.xPosition, node.yPosition));
  bundles.forEach(bundle =>
    bundle.segments.forEach(seg => {
      grow(bounds, seg.startPoint.x, seg.startPoint.y);
      grow(bounds, seg.endPoint.x, seg.endPoint.y);
    })
  );

  if (!Number.isFinite(bounds.minX)) {
    // Nothing to draw yet: emit a valid, empty canvas.
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"></svg>';
  }

  const minX = bounds.minX - PADDING;
  const minY = bounds.minY - PADDING;
  const width = bounds.maxX - bounds.minX + PADDING * 2;
  const height = bounds.maxY - bounds.minY + PADDING * 2;

  const wireLayer = bundles.map(bundle => bundle.segments.map(segmentSvg).join('\n')).join('\n');
  const bowLayer = bundles.map(bowSvg).join('\n');

  const connectorLayer = connectorBoxes
    .map(({ connector, height: h }) => {
      const pinCount = (connector.pins ?? []).length;
      const subtitle = pinCount === 1 ? '1 pin' : `${pinCount} pins`;
      return (
        `<g>` +
        `<rect x="${round(connector.xPosition)}" y="${round(connector.yPosition)}" ` +
        `width="${CONNECTOR.WIDTH}" height="${round(h)}" rx="6" ` +
        `fill="#ffffff" stroke="${COLORS.HEADER}" stroke-width="1.5" />` +
        `<rect x="${round(connector.xPosition)}" y="${round(connector.yPosition)}" ` +
        `width="${CONNECTOR.WIDTH}" height="26" rx="6" fill="${COLORS.HEADER}" />` +
        `<text x="${round(connector.xPosition + 10)}" y="${round(connector.yPosition + 17)}" ` +
        `font-size="13" font-weight="600" fill="#ffffff">${escapeXml(connector.name)}</text>` +
        `<text x="${round(connector.xPosition + CONNECTOR.WIDTH - 10)}" y="${round(connector.yPosition + 17)}" ` +
        `text-anchor="end" font-size="11" fill="${COLORS.PRIMARY}">${subtitle}</text>` +
        `</g>`
      );
    })
    .join('\n');

  const nodeLayer = state.nodes
    .map(
      node =>
        `<circle cx="${round(node.xPosition)}" cy="${round(node.yPosition)}" r="${NODE_RADIUS}" ` +
        `fill="${COLORS.SECONDARY}" />`
    )
    .join('\n');

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${round(minX)} ${round(minY)} ${round(width)} ${round(height)}" ` +
    `font-family="sans-serif">\n` +
    `${backgroundSvg(state.background)}\n${wireLayer}\n${bowLayer}\n${nodeLayer}\n${connectorLayer}\n` +
    `</svg>`
  );
}
